import React from "react";
import { CellClickedEvent, ColDef, CellClassParams, ITooltipParams, GetQuickFilterTextParams, ColGroupDef, ValueFormatterParams, ValueGetterParams } from "@ag-grid-community/all-modules";
import RenderParameters from "./renderParameters";
import { Form } from "react-bootstrap";
import { formatDate, formatDecimalPlace, formatUTCDate } from "common/formatters";
import SelectAllHeader, { SelectAllHeaderProps } from "./selectAllHeader";
import type { ConditionalKeys } from 'type-fest';
import type { AgGridField, AgGridFieldValue, AgGridValueOrFallback, WithTypedAgGridDataAndValue } from "./utilityTypes";

type ColDefOptions = Omit<ColDef,
    | "cellRenderer"
    | "valueGetter"
    | "getQuickFilterText"
    | "valueFormatter"
    | "cellClass"
    | "cellClassRules"
    | "filterValueGetter"
    | "cellRendererSelector"
>

export class ColumnBuilder<Data> {
    build(header: string, field?: AgGridField<Data>, options?: ColDefOptions): ColDef {
        return {
            headerName: header,
            field,
            ...options
        };
    }

    collection(builderFunc: (b: ColumnBuilder<Data>) => (ColDefBuilder<Data, unknown> | ColGroupDefBuilder)[]): ColDef[] {
        const builder = new ColumnBuilder<Data>();

        return builderFunc(builder).map(m => m.build());
    }

    for<Field extends AgGridField<Data> = "">(header: string, field?: Field, options?: ColDefOptions): ColDefBuilder<Data, AgGridFieldValue<Data, Field>> {
        return new ColDefBuilder({
            headerName: header,
            field,
            ...options
        })
    }

    group(header: string, children: ColDef[], options?: ColDefOptions | ColGroupDef): ColGroupDefBuilder {
        return new ColGroupDefBuilder({
            headerName: header,
            children: children,
            ...options
        });
    }

    buildCheckbox(header: string, field: ConditionalKeys<Data, boolean>, onChange: (data: Data, isChecked: boolean) => void, options?: ColDefOptions): ColDef {
        return this.for(header, field, options)
            .withoutSearchFilter()
            .withCellRenderer(params => (
                <Form.Check
                    name={field} id={field + params.node.id}
                    custom label=""
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => onChange(params.node.data, event.target.checked)}
                    checked={params.node.data[field] as boolean}
                    data-testid={field ? `${field}-checkbox` : undefined}
                />
            ))
            .build();
    }
}

export class ColDefBuilder<Data, Value> {
    constructor(private colDef: ColDef) { }

    asNumber(): this {
        this.colDef.valueFormatter = 'value.toLocaleString()';
        this.colDef.type = "numericColumn";
        return this;
    }

    asDecimal(placeholder?: string): this {
        this.colDef.valueFormatter = params => params.value == null ? placeholder : formatDecimalPlace(params.value);
        this.colDef.type = "numericColumn";
        return this;
    }

    asUtcDate(format?: string): this {
        this.colDef.valueFormatter = params => formatUTCDate(params.value, format);
        return this;
    }

    asDate(format?: string): this {
        this.colDef.valueFormatter = params => formatDate(params.value, format);
        return this;
    }

    asDateTime(): this {
        return this.asDate("MM/DD/YY LTS")
    }

    asDraggable(isDraggable?: boolean): this {
        this.colDef.rowDrag = isDraggable ?? true;
        return this;
    }

    asRightAligned(): this {
        this.colDef.type = 'rightAligned';
        return this;
    }

    withCellClass(className: string | string[] | ((params: WithTypedAgGridDataAndValue<CellClassParams, Data, Value>) => string | string[] | null | undefined)) {
        this.colDef.cellClass = className;
        return this;
    }

    withCellClassRules(classRules: Record<string, string | ((params: WithTypedAgGridDataAndValue<CellClassParams, Data, Value>) => boolean)>) {
        this.colDef.cellClassRules = classRules;
        return this;
    }

    withWidth(minWidth: number, width: number = null): this {
        this.colDef.width = width ?? minWidth;
        this.colDef.minWidth = minWidth;
        return this;
    }

    withFlex(flex: number): this {
        this.colDef.flex = flex;
        return this;
    }

    withGetter<T>(getter: (params: WithTypedAgGridDataAndValue<ValueGetterParams, Data, Value>) => T): ColDefBuilder<Data, T> {
        this.colDef.valueGetter = m => {
            try {
                return getter(m as unknown as WithTypedAgGridDataAndValue<ValueGetterParams, Data, Value>);
            }
            catch {
                return null;
            }
        };
        // recast the value type to T so future method calls are type-safe
        // (any previous methods using Value will not have the correct type, so withGetter should be called first)
        return this as unknown as ColDefBuilder<Data, T>;
    }

    withFilterGetter(getter: (input: Data) => string): this {
        this.colDef.filterValueGetter = params => {
            try {
                return getter(params.data);
            }
            catch {
                return null;
            }
        };
        return this;
    }

    //Used to check if the cell needs to refresh
    withEquals(comparator: (input1: AgGridValueOrFallback<Data, Value>, input2: AgGridValueOrFallback<Data, Value>) => boolean): this {
        this.colDef.equals = comparator;
        return this;
    }

    //Used for sorting
    withComparator(comparator: (input1: AgGridValueOrFallback<Data, Value>, input2: AgGridValueOrFallback<Data, Value>) => number): this {
        this.colDef.comparator = comparator;
        return this;
    }

    withTooltipValueGetter(getter: (input: ITooltipParams & { data?: Data; value?: Value; node?: { data?: Data } }) => string | number | null | undefined): this {
        this.colDef.tooltipValueGetter = getter;
        return this;
    }

    withFormatter(formatter: (input: Value) => string): this {
        this.colDef.valueFormatter = params => params.node.group ? null : formatter(params.value);
        return this;
    }

    withFormatterParams(formatter: (params: WithTypedAgGridDataAndValue<ValueFormatterParams, Data, Value>) => string): this {
        this.colDef.valueFormatter = formatter;
        return this;
    }

    withSort(sort: ("asc" | "desc")): this {
        this.colDef.sort = sort;
        return this;
    }

    withHeaderComponent<Props>(component: React.FC<Props>, props: Props = null): this {
        this.colDef.headerComponent = component;
        this.colDef.headerComponentParams = props;
        return this;
    }

    withSelectAllHeader(props: SelectAllHeaderProps): this {
        return this.withHeaderComponent(SelectAllHeader, props);
    }

    withCellRenderer(renderer: (params: RenderParameters<Data, Value>) => React.ReactNode): this {
        this.colDef.cellRenderer = (params: RenderParameters<Data, Value>) => {
            try {
                return renderer(params);
            }
            catch {
                return null;
            }
        };
        return this;
    }

    onCellClick(handler: ((ev: CellClickedEvent) => void)): this {
        this.colDef.onCellClicked = handler;
        return this;
    }

    withCellRendererParams(params: unknown): this {
        this.colDef.cellRendererParams = params;
        return this;
    }

    withValueCellRenderer(renderer: (params: RenderParameters<Data, Value>) => React.ReactNode): this {
        this.colDef.cellRenderer = renderer;
        return this;
    }

    withPinnedRowCellRenderer(renderer: (params: RenderParameters<Data, Value>) => React.ReactNode): this {
        this.colDef.cellRendererSelector = params => {
            if (params.node.rowPinned) {
                return {
                    component: renderer
                }
            }
            return null;
        }
        return this;
    }

    flashOnChange(): this {
        this.colDef.enableCellChangeFlash = true;
        return this;
    }

    withColumnClass(cellClassName: string | string[], headerClassName: string | string[]) {
        this.colDef.headerClass = headerClassName;
        this.colDef.cellClass = cellClassName;
        return this;
    }

    withSearchFilter(func: ((params: WithTypedAgGridDataAndValue<GetQuickFilterTextParams, Data, Value>) => string)): this {
        this.colDef.getQuickFilterText = func;
        return this;
    }

    withoutSearchFilter(): this {
        this.colDef.getQuickFilterText = getEmptyString;
        return this;
    }

    build(): ColDef {
        return this.colDef;
    }
}

export class ColGroupDefBuilder {
    constructor(private colGroupDef: ColGroupDef) { }

    build(): ColDef {
        return this.colGroupDef;
    }
}

function getEmptyString(): string { return ""; }
